/**
 * Pure view builder for the dashboard's cliffhanger resume card.
 *
 * Turns a `ResumeContext` (plus viewer facts) into everything the card
 * renders: status line, recap snippet, art URL chain, CTA label + kind.
 * No I/O, no store imports, unit-testable in isolation.
 */

import type { ResumeContext } from './api';

/**
 * One-shot lobby auto-start handoff key (sessionStorage). Written by the
 * resume card's Continue CTA, consumed by LobbyLayout's auto-start effect.
 */
export const RESUME_AUTO_START_KEY = 'resume-auto-start';

export type ResumeCtaKind =
  /** Navigate straight into the live session (`/play/:id`). */
  | 'resume-live'
  /** Leader restarts the adventure via lobby auto-start. */
  | 'continue-adventure'
  /** Non-leader returns to the lobby and waits for the leader. */
  | 'return-to-camp';

export interface ResumeCardView {
  /** Card headline ("Resume: mid-fight in The Founder's Tomb"). */
  title: string;
  /** Secondary line ("Previously on Goldvale's Last Hope..."). */
  subtitle: string | null;
  /** Recap snippet (ENDED state), trimmed to a sentence boundary. */
  recap: string | null;
  /** Resolved art URL, or null for the no-art fallback panel. */
  artUrl: string | null;
  ctaKind: ResumeCtaKind;
  ctaLabel: string;
  /** Session id for resume-live navigation. */
  activeSessionId: string | null;
}

export interface ResumeCardInputs {
  context: ResumeContext;
  /** The viewer's character leads the party (drives the ENDED CTA). */
  isLeader: boolean;
  /** Resolve a raw `data/modules/...` media path to a CDN URL. */
  resolveMediaPath: (path: string) => string;
  /** Module hero image URL fallback (by module id). */
  moduleHeroUrl: (moduleId: string) => string;
}

/** Max recap characters shown on the card. */
export const RECAP_SNIPPET_MAX_CHARS = 220;

/**
 * Trim a recap to at most `max` chars, preferring the last sentence
 * boundary; falls back to a word boundary with an ellipsis.
 */
/** The "Previously on" summary is LLM prose with markdown emphasis (`**Ren**`).
 *  The dashboard card renders it as plain text, so strip the emphasis markers
 *  (keeping the words + emoji) rather than leak literal asterisks. Runs before
 *  truncation so a cut can't strand half a `**` marker. */
function stripEmphasis(s: string): string {
  return s.replace(/\*\*(.+?)\*\*/g, '$1').replace(/\*([^*]+?)\*/g, '$1');
}

export function recapSnippet(summary: string, max: number = RECAP_SNIPPET_MAX_CHARS): string {
  const text = stripEmphasis(summary).trim();
  if (text.length <= max) {
    return text;
  }
  const window = text.slice(0, max);
  const lastSentenceEnd = Math.max(
    window.lastIndexOf('. '),
    window.lastIndexOf('! '),
    window.lastIndexOf('? ')
  );
  if (lastSentenceEnd > max * 0.4) {
    return window.slice(0, lastSentenceEnd + 1);
  }
  const lastSpace = window.lastIndexOf(' ');
  return `${window.slice(0, lastSpace > 0 ? lastSpace : max)}...`;
}

/**
 * Resolve a snapshot scene `media_path` to a displayable URL.
 *
 * The snapshot stores scene art as a FULL CDN URL (copied from the module
 * scene `image_url`, or a dynamically generated scene image), not a
 * `data/modules/` relative path. Absolute URLs pass through untouched;
 * double-prepending the bucket produced a broken URL and blank art. Only a
 * legacy relative path is mapped onto the media bucket.
 */
export function resolveSceneMediaUrl(path: string, bucketUrl: string): string {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }
  const stripped = path.replace(/^data\/modules\//, '');
  return `${bucketUrl}/static/modules/${stripped}`;
}

/** Human status phrase for a live session's encounter type. The engine runs two
 *  player-facing modes: combat and explore (explore subsumes all social/roleplay),
 *  so we only split mid-fight vs exploring. */
function liveStatusPhrase(
  encounterType: string | undefined,
  sceneName: string | undefined
): string {
  const scene = sceneName ? ` in ${sceneName}` : '';
  return encounterType === 'combat' ? `Resume: mid-fight${scene}` : `Resume: exploring${scene}`;
}

/**
 * Build the card view, or null when there is nothing to show
 * (`state: 'none'`).
 */
export function buildResumeCard(inputs: ResumeCardInputs): ResumeCardView | null {
  const { context, isLeader, resolveMediaPath, moduleHeroUrl } = inputs;

  if (context.state === 'none') {
    return null;
  }

  // Art fallback chain: snapshot scene media -> module hero -> none.
  const artUrl = context.sceneMediaPath
    ? resolveMediaPath(context.sceneMediaPath)
    : context.moduleId
      ? moduleHeroUrl(context.moduleId)
      : null;

  if (context.state === 'live') {
    return {
      title: liveStatusPhrase(context.encounterType, context.sceneName),
      subtitle: context.moduleName ?? null,
      recap: null,
      artUrl,
      ctaKind: 'resume-live',
      ctaLabel: 'Resume Adventure',
      activeSessionId: context.activeSessionId ?? null,
    };
  }

  // ENDED_INCOMPLETE: "Previously on..." + recap + leader-aware CTA.
  const moduleName = context.moduleName ?? context.lastSession?.moduleName;
  const summary = context.lastSession?.summary;
  return {
    title: moduleName ? `Previously on ${moduleName}...` : 'Your adventure awaits',
    subtitle: context.sceneName ? `Last seen: ${context.sceneName}` : null,
    recap: summary ? recapSnippet(summary) : null,
    artUrl,
    ctaKind: isLeader ? 'continue-adventure' : 'return-to-camp',
    ctaLabel: isLeader ? 'Continue the Adventure' : 'Return to Camp',
    activeSessionId: null,
  };
}
